import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';

import { CONTACT_INFO_MAX_LENGTH } from '@app/constants/contactInfoMaxLength';
import { removeNotNumberFromString } from '@app/libs/inputFormat';
import { validateEmail, validateTitle } from '@app/libs/validation';
import validatePassword from '@app/libs/validation/certPassword';
import validatePdfPassword from '@app/libs/validation/pdfPassword';
import ColumnType from '@app/store/bulkRequest/form/ColumnType';

export const bizNumberRegExp = /^([1-9]{1})\d{2}[-]?\d{2}[-]?\d{5}$/;

export function validateColumnValue({
  columnIndex,
  rowIndex,
  type,
  value,
  othersContactInfosInTemplate,
  isSigningInOrder,
}: {
  columnIndex: number;
  rowIndex: number;
  type: ColumnType;
  value: string;
  othersContactInfosInTemplate: string[];
  isSigningInOrder: boolean;
}) {
  const participantNameRegExp = /[/?<>\\":*|₩]/g;

  if (value === 'undefined') {
    return {
      column: columnIndex,
      message:
        '데이터를 읽을 수 없습니다. 엑셀양식을 다시 확인해 주세요. 사용자 서식을 이용 시 문제가 발생할 수 있습니다.',
      row: rowIndex,
      type,
    };
  }

  if (type === 'CARBON_COPY_CONTACT' && !!value) {
    const contactList = value.split(',');
    if (contactList.length > 10) {
      return {
        column: columnIndex,
        message: '참조자는 최대 10명까지 추가할 수 있습니다.',
        row: rowIndex,
        type,
      };
    }

    const hasContactValidationError =
      contactList.filter(
        (contact) => !isEmail(contact.trim()) && !isMobilePhone(contact.trim(), 'ko-KR')
      ).length > 0;

    if (hasContactValidationError) {
      return {
        column: columnIndex,
        message:
          "올바른 이메일 또는 휴대폰 번호를 입력해 주세요. 여러명일 경우 ','로 구분해 주세요.",
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'DOCUMENT_TITLE') {
    if (value.length < 2 || value.length > 100) {
      return {
        column: columnIndex,
        message: '문서명은 2자 이상 100자 이하로 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }

    const { message, success } = validateTitle(value);
    if (!success) {
      return {
        column: columnIndex,
        message,
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'PARTICIPANT_NAME') {
    if (value.length < 2 || value.length > 30) {
      return {
        column: columnIndex,
        message: '서명자명은 2자 이상 30자 이하로 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }
    if (participantNameRegExp.test(value)) {
      return {
        column: columnIndex,
        message: '서명자 이름에 /?<>\\":*|₩를 입력할 수 없습니다.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'PARTICIPANT_CONTACT') {
    if (value.includes(' ')) {
      return {
        column: columnIndex,
        message:
          "이메일 또는 휴대전화 번호에 공백(' ')이 포함되어 있습니다. 올바른 이메일 또는 휴대전화 번호를 입력해 주세요.",
        row: rowIndex,
        type,
      };
    }

    if (value.length > CONTACT_INFO_MAX_LENGTH) {
      return {
        column: columnIndex,
        message: `연락처는 ${CONTACT_INFO_MAX_LENGTH}자 이내로 입력해 주세요.`,
        row: rowIndex,
        type,
      };
    }

    if (!validateEmail(value).success && !isMobilePhone(value, 'ko-KR')) {
      return {
        column: columnIndex,
        message: '올바른 이메일 또는 휴대전화 번호를 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }
    if (isMobilePhone(value, 'ko-KR')) {
      if (removeNotNumberFromString(value).length !== 11 || !value.startsWith('010')) {
        return {
          column: columnIndex,
          message: '올바른 이메일 또는 휴대전화 번호를 입력해 주세요.',
          row: rowIndex,
          type,
        };
      }
    }

    if (!isSigningInOrder && othersContactInfosInTemplate.includes(value)) {
      return {
        column: columnIndex,
        message: '중복된 이메일 또는 번호가 있습니다. 다른 연락처를 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'MESSAGE_FROM_REQUESTER') {
    if (value.length > 1000) {
      return {
        column: columnIndex,
        message: '남길 말은 1000자 이하로 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'VERIFICATION_PASSWORD') {
    const passwordValidateErrorMessage = validatePassword(value);
    if (value && passwordValidateErrorMessage) {
      return {
        column: columnIndex,
        message: `접근 암호는 ${passwordValidateErrorMessage}`,
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'VERIFICATION_PASSWORD_HINT') {
    if (value && value.length > 30) {
      return {
        column: columnIndex,
        message: '접근 암호 힌트는 30자 이하로 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'VERIFICATION_MOBILE_IDENTIFICATION_NAME') {
    if (value && value.length > 30) {
      return {
        column: columnIndex,
        message: '휴대폰 본인인증 이름은 30자 이하로 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'VERIFICATION_MOBILE_IDENTIFICATION_PHONE') {
    if (value && !isMobilePhone(value, 'ko-KR')) {
      return {
        column: columnIndex,
        message: "올바른 휴대전화번호를 입력해주세요.('-'없이 숫자만)",
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'PDF_PASSWORD') {
    if (/\s/.test(value)) {
      return {
        column: columnIndex,
        message:
          "완료문서 잠금 비밀번호에 공백(' ')이 포함되어 있습니다. 올바른 비밀번호를 설정해주세요.",
        row: rowIndex,
        type,
      };
    }

    if (!!value && !validatePdfPassword(value).success) {
      return {
        column: columnIndex,
        message: '완료문서 잠금 비밀번호는 6자~32자로 설정해주세요.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'VERIFICATION_D_CERT_NAME') {
    if (value && (value.length > 30 || value.length < 2)) {
      return {
        column: columnIndex,
        message: '법인명은 2자 이상 30자 이하로 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'VERIFICATION_D_CERT_BIZ_NUMBER') {
    // 사업자 등록번호는 xxx-xx-xxxxx 또는 10자리의 숫자 형식이며, 첫 일련번호 3자리는 101-999의 범위를 가집니다.
    if (value && !bizNumberRegExp.test(value)) {
      return {
        column: columnIndex,
        message: '올바른 사업자등록번호를 입력해 주세요. (ex. 123-45-67890)',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'FIELD_CHECKBOX') {
    if (value !== 'TRUE' && value !== 'FALSE') {
      return {
        column: columnIndex,
        message: '체크를 하시려면 O를 체크를 하지 않으시려면 X를 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }
  }

  if (type === 'FIELD_TEXT') {
    if (value && value.length > 300) {
      return {
        column: columnIndex,
        message: '텍스트 입력은 300자 이하로 입력해 주세요.',
        row: rowIndex,
        type,
      };
    }
  }

  return null;
}
